import { useState, useCallback } from 'react';
import { Upload, X, Check, AlertCircle, ChevronRight, ChevronLeft } from 'lucide-react';
import { parseCSV } from '@/hooks/useBulkOperations';
import { cn } from '@/lib/utils';

interface ColumnDefinition {
  key: string;
  label: string;
  required?: boolean;
}

interface BulkImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  columns: ColumnDefinition[];
  onImport: (data: Record<string, unknown>[]) => Promise<{ success: number; errors: Array<{ row: number; error: string }> }>;
  transformRow: (row: string[], mapping: Record<string, number>) => Record<string, unknown>;
}

type Step = 'upload' | 'mapping' | 'preview' | 'importing' | 'results';

export function BulkImportModal({
  isOpen,
  onClose,
  title,
  columns,
  onImport,
  transformRow,
}: BulkImportModalProps) {
  const [step, setStep] = useState<Step>('upload');
  const [parsedRows, setParsedRows] = useState<string[][]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [columnMapping, setColumnMapping] = useState<Record<string, number>>({});
  const [importResults, setImportResults] = useState<{ success: number; errors: Array<{ row: number; error: string }> } | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const reset = useCallback(() => {
    setStep('upload');
    setParsedRows([]);
    setHeaders([]);
    setColumnMapping({});
    setImportResults(null);
    setIsProcessing(false);
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;

      const rows = parseCSV(content);
      if (rows.length > 0) {
        setHeaders(rows[0]);
        setParsedRows(rows.slice(1));
        // Auto-map columns by matching header names
        const mapping: Record<string, number> = {};
        columns.forEach((col) => {
          const idx = rows[0].findIndex(
            (h) => h.toLowerCase().replace(/[_\s]/g, '') === col.key.toLowerCase().replace(/[_\s]/g, '')
          );
          if (idx >= 0) {
            mapping[col.key] = idx;
          }
        });
        setColumnMapping(mapping);
        setStep('mapping');
      }
    };
    reader.readAsText(file);
  }, [columns]);

  const handleMappingChange = useCallback((columnKey: string, headerIndex: number) => {
    setColumnMapping((prev) => {
      if (headerIndex === -1) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [columnKey]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [columnKey]: headerIndex };
    });
  }, []);

  const canProceedToPreview = useCallback(() => {
    return columns.filter((c) => c.required).every((c) => columnMapping[c.key] !== undefined);
  }, [columns, columnMapping]);

  const handleImport = useCallback(async () => {
    setIsProcessing(true);
    setStep('importing');

    try {
      const data = parsedRows.map((row) => transformRow(row, columnMapping));
      const validData = data.filter((d) => d !== null);
      const results = await onImport(validData);
      setImportResults(results);
      setStep('results');
    } catch (error: any) {
      setImportResults({
        success: 0,
        errors: [{ row: 0, error: error.message || 'Import failed' }],
      });
      setStep('results');
    } finally {
      setIsProcessing(false);
    }
  }, [parsedRows, columnMapping, transformRow, onImport]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between max-w-md mx-auto">
            {(['upload', 'mapping', 'preview', 'results'] as const).map((s, i) => (
              <div key={s} className="flex items-center">
                <div
                  className={cn(
                    'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium',
                    step === s || (['upload', 'mapping', 'preview'].indexOf(step) >= i)
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-500'
                  )}
                >
                  {i + 1}
                </div>
                <span className={cn('ml-2 text-sm hidden sm:block', step === s ? 'text-blue-600 font-medium' : 'text-gray-500')}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </span>
                {i < 3 && <ChevronRight className="h-4 w-4 text-gray-300 mx-2" />}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 1: Upload */}
          {step === 'upload' && (
            <div className="text-center py-8">
              <Upload className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Upload CSV File</h3>
              <p className="text-sm text-gray-500 mb-6">
                Select a CSV file to import. The first row should contain column headers.
              </p>
              <label className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer">
                <Upload className="h-4 w-4" />
                Choose File
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </label>
            </div>
          )}

          {/* Step 2: Column Mapping */}
          {step === 'mapping' && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Map Columns</h3>
              <p className="text-sm text-gray-500 mb-6">
                Map your CSV columns to the required fields. Required fields are marked with *.
              </p>
              <div className="space-y-3">
                {columns.map((col) => (
                  <div key={col.key} className="flex items-center gap-4">
                    <label className="w-40 text-sm font-medium text-gray-700">
                      {col.label}
                      {col.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    <select
                      value={columnMapping[col.key] ?? -1}
                      onChange={(e) => handleMappingChange(col.key, parseInt(e.target.value))}
                      className="input flex-1"
                    >
                      <option value={-1}>-- Select column --</option>
                      {headers.map((h, i) => (
                        <option key={i} value={i}>
                          {h || `Column ${i + 1}`}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Preview */}
          {step === 'preview' && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Preview Data</h3>
              <p className="text-sm text-gray-500 mb-4">
                Review the first 5 rows before importing. {parsedRows.length} total rows will be imported.
              </p>
              <div className="overflow-x-auto border rounded-lg">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                      {columns.filter((c) => columnMapping[c.key] !== undefined).map((col) => (
                        <th key={col.key} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {parsedRows.slice(0, 5).map((row, i) => (
                      <tr key={i}>
                        <td className="px-3 py-2 text-gray-500">{i + 1}</td>
                        {columns.filter((c) => columnMapping[c.key] !== undefined).map((col) => (
                          <td key={col.key} className="px-3 py-2 text-gray-700">
                            {row[columnMapping[col.key]] || '-'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Step 4: Importing */}
          {step === 'importing' && (
            <div className="text-center py-12">
              <div className="animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900">Importing...</h3>
              <p className="text-sm text-gray-500">Please wait while we process your data.</p>
            </div>
          )}

          {/* Step 5: Results */}
          {step === 'results' && importResults && (
            <div>
              <div className={cn(
                'rounded-lg p-6 mb-6',
                importResults.errors.length === 0 ? 'bg-green-50' : 'bg-yellow-50'
              )}>
                <div className="flex items-center gap-3">
                  {importResults.errors.length === 0 ? (
                    <Check className="h-8 w-8 text-green-500" />
                  ) : (
                    <AlertCircle className="h-8 w-8 text-yellow-500" />
                  )}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      Import Complete
                    </h3>
                    <p className="text-sm text-gray-600">
                      Successfully imported {importResults.success} records
                      {importResults.errors.length > 0 && ` with ${importResults.errors.length} errors`}
                    </p>
                  </div>
                </div>
              </div>

              {importResults.errors.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Errors:</h4>
                  <div className="max-h-48 overflow-y-auto border rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Row</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Error</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {importResults.errors.map((err, i) => (
                          <tr key={i}>
                            <td className="px-3 py-2 text-gray-500">{err.row}</td>
                            <td className="px-3 py-2 text-red-600">{err.error}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div>
            {step !== 'upload' && step !== 'results' && step !== 'importing' && (
              <button
                onClick={() => {
                  if (step === 'mapping') setStep('upload');
                  if (step === 'preview') setStep('mapping');
                }}
                className="btn-secondary flex items-center gap-1"
              >
                <ChevronLeft className="h-4 w-4" />
                Back
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button onClick={handleClose} className="btn-secondary">
              {step === 'results' ? 'Close' : 'Cancel'}
            </button>
            {step === 'mapping' && (
              <button
                onClick={() => setStep('preview')}
                disabled={!canProceedToPreview()}
                className="btn-primary flex items-center gap-1 disabled:opacity-50"
              >
                Preview
                <ChevronRight className="h-4 w-4" />
              </button>
            )}
            {step === 'preview' && (
              <button
                onClick={handleImport}
                disabled={isProcessing}
                className="btn-primary flex items-center gap-1"
              >
                <Upload className="h-4 w-4" />
                Import {parsedRows.length} Rows
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
